import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { AyahData } from '../../types';
import { fetchAyah, precacheAyah } from '../../api/client';

const SURAH_AYAH_COUNTS: Record<number, number> = {
  1: 7, 2: 286, 3: 200, 4: 176, 5: 120, 6: 165, 7: 206, 8: 75, 9: 129, 10: 109,
  11: 123, 12: 111, 13: 43, 14: 52, 15: 99, 16: 128, 17: 111, 18: 110, 19: 98, 20: 135,
  21: 112, 22: 78, 23: 118, 24: 64, 25: 77, 26: 227, 27: 93, 28: 88, 29: 69, 30: 60,
  31: 34, 32: 30, 33: 73, 34: 54, 35: 45, 36: 83, 37: 182, 38: 88, 39: 75, 40: 85,
  41: 54, 42: 53, 43: 89, 44: 59, 45: 37, 46: 35, 47: 38, 48: 29, 49: 18, 50: 45,
  51: 60, 52: 49, 53: 62, 54: 55, 55: 78, 56: 96, 57: 29, 58: 22, 59: 24, 60: 13,
  61: 14, 62: 11, 63: 11, 64: 18, 65: 12, 66: 12, 67: 30, 68: 52, 69: 52, 70: 44,
  71: 28, 72: 28, 73: 20, 74: 56, 75: 40, 76: 31, 77: 50, 78: 40, 79: 46, 80: 42,
  81: 29, 82: 19, 83: 36, 84: 25, 85: 22, 86: 17, 87: 19, 88: 26, 89: 30, 90: 20,
  91: 15, 92: 21, 93: 11, 94: 8, 95: 8, 96: 19, 97: 5, 98: 8, 99: 8, 100: 11,
  101: 11, 102: 8, 103: 3, 104: 9, 105: 5, 106: 4, 107: 7, 108: 3, 109: 6, 110: 3,
  111: 5, 112: 4, 113: 5, 114: 6,
};

export function getTotalAyahs(surah: number): number {
  return SURAH_AYAH_COUNTS[surah] ?? 1;
}

function cacheKey(surah: number, ayah: number): string {
  return `${surah}:${ayah}`;
}

// ─── State ─────────────────────────────────────────────────────

interface AyahState {
  current: AyahData | null;
  selectedSurah: number;
  selectedAyah: number;
  totalAyahs: number;
  /** Ayah cache: "surah:ayah" → AyahData. Persists across navigation. */
  cache: Record<string, AyahData>;
  loading: boolean;
  error: string | null;
}

const initialState: AyahState = {
  current: null,
  selectedSurah: 1,
  selectedAyah: 1,
  totalAyahs: 7,
  cache: {},
  loading: false,
  error: null,
};

// ─── Thunks ────────────────────────────────────────────────────

/**
 * Navigate to a specific ayah. Uses cache if available (instant),
 * otherwise fetches from API. Always prefetches nearby ayahs in the background.
 */
export const navigateToAyah = createAsyncThunk(
  'ayah/navigateToAyah',
  async (
    { surah, ayah }: { surah: number; ayah: number },
    { getState, dispatch }
  ) => {
    const state = (getState() as { ayah: AyahState }).ayah;
    const key = cacheKey(surah, ayah);
    const totalAyahs = getTotalAyahs(surah);

    // If cached, return immediately — no network wait
    const cached = state.cache[key];
    if (cached) {
      // Kick off prefetch in background (non-blocking)
      dispatch(prefetchNearby({ surah, ayah, totalAyahs }));
      // Warm Gemini's per-ayah cache so the eval is a cache HIT
      precacheAyah(surah, ayah);
      return { data: cached, totalAyahs, fromCache: true };
    }

    // Not cached — fetch this ayah
    const data = await fetchAyah(surah, ayah);

    // Kick off prefetch in background (non-blocking)
    dispatch(prefetchNearby({ surah, ayah, totalAyahs }));
    // Warm Gemini's per-ayah cache so the eval is a cache HIT
    precacheAyah(surah, ayah);

    return { data, totalAyahs, fromCache: false };
  }
);

/**
 * Prefetch ayahs around the current position. Fires and forgets —
 * results go into cache silently, no loading state.
 */
export const prefetchNearby = createAsyncThunk(
  'ayah/prefetchNearby',
  async (
    { surah, ayah, totalAyahs }: { surah: number; ayah: number; totalAyahs: number },
    { getState }
  ) => {
    const state = (getState() as { ayah: AyahState }).ayah;

    // Prefetch next 3 + prev 1 (user mostly goes forward)
    const targets: number[] = [];
    for (let i = ayah + 1; i <= Math.min(ayah + 3, totalAyahs); i++) targets.push(i);
    if (ayah > 1) targets.push(ayah - 1);

    // Filter out already-cached
    const toFetch = targets.filter((a) => !state.cache[cacheKey(surah, a)]);
    if (toFetch.length === 0) return [];

    // Fetch in parallel, swallow individual errors
    const results = await Promise.allSettled(
      toFetch.map((a) => fetchAyah(surah, a).then((data) => ({ key: cacheKey(surah, a), data })))
    );

    return results
      .filter((r): r is PromiseFulfilledResult<{ key: string; data: AyahData }> => r.status === 'fulfilled')
      .map((r) => r.value);
  }
);

// ─── Slice ─────────────────────────────────────────────────────

const ayahSlice = createSlice({
  name: 'ayah',
  initialState,
  reducers: {
    setSurah(state, action: PayloadAction<number>) {
      state.selectedSurah = action.payload;
      state.selectedAyah = 1;
      state.totalAyahs = getTotalAyahs(action.payload);
    },
    setAyah(state, action: PayloadAction<number>) {
      state.selectedAyah = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // navigateToAyah
      .addCase(navigateToAyah.pending, (state, action) => {
        const { surah, ayah } = action.meta.arg;
        const key = cacheKey(surah, ayah);
        // If cached, set current immediately — no loading spinner
        if (state.cache[key]) {
          state.current = state.cache[key];
          state.selectedSurah = surah;
          state.selectedAyah = ayah;
          state.totalAyahs = getTotalAyahs(surah);
          state.loading = false;
          state.error = null;
        } else {
          state.loading = true;
          state.error = null;
          state.selectedSurah = surah;
          state.selectedAyah = ayah;
          state.totalAyahs = getTotalAyahs(surah);
        }
      })
      .addCase(navigateToAyah.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.data;
        state.selectedSurah = action.payload.data.surah;
        state.selectedAyah = action.payload.data.ayah;
        state.totalAyahs = action.payload.totalAyahs;
        // Add to cache
        const key = cacheKey(action.payload.data.surah, action.payload.data.ayah);
        state.cache[key] = action.payload.data;
      })
      .addCase(navigateToAyah.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load ayah';
      })

      // prefetchNearby — silently populate cache
      .addCase(prefetchNearby.fulfilled, (state, action) => {
        for (const { key, data } of action.payload) {
          state.cache[key] = data;
        }
      });
  },
});

export const { setSurah, setAyah } = ayahSlice.actions;

// Keep loadAyah as an alias for navigateToAyah so existing imports don't break
export const loadAyah = navigateToAyah;

export default ayahSlice.reducer;
